// import SubtleBadge from "components/common/SubtleBadge";

import SubtleBadge from "components/common/SubtleBadge";
import { useFetchCategories } from "hooks/useFetchCategories";
import { useFetchStores } from "hooks/useFetchStores";
import { useEffect, useState, useRef } from "react";
import {
  Button,
  Col,
  FloatingLabel,
  Form,
  InputGroup,
  OverlayTrigger,
  Row,
  Tooltip,
} from "react-bootstrap";
import { useFormContext } from "react-hook-form";
import { MdOutlineLibraryAddCheck } from "react-icons/md";
import { useLocation } from "react-router-dom";
import Select from "react-select";
import { toast } from "react-toastify";
import Swal from "sweetalert2";
import { customSettings } from "utils/customConfig";
import CustomOption from "utils/CustomOption";
import { useFetchFieldSetsByCategory } from "hooks/useFetchFieldSetsByCategory";
import FieldsModal from "./FieldsModal";
import { HiOutlineViewfinderCircle } from "react-icons/hi2";
import { useEditData } from "../../EditContext";

const ProductGeneralInfo = () => {
  const {
    register,
    getValues,
    watch,
    clearErrors,
    setValue,
    formState: { errors },
  } = useFormContext();

  const state = useEditData();
  // const { state } = useLocation();
  const categoryId = watch("categoryId");
  const customizable = watch("isCustomizable");
  const customizationId = watch("customizationId");

  const [tags, setTags] = useState(getValues("tags") || []);
  const [inputValue, setInputValue] = useState("");
  const [isTagsFocused, setIsTagsFocused] = useState(false);
  const tagsInputRef = useRef(null);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [fieldsModal, setFieldsModal] = useState(false);

  const { data: categoriesData, isLoading: categoriesLoading } =
    useFetchCategories();
  const { data: storesData } = useFetchStores();
  const { data: fieldSetData, isLoading: fieldSetLoading } =
    useFetchFieldSetsByCategory(customizable === true, Number(categoryId));

  // Custom styles for react-select with floating label look
  const customStyles = {
    container: (provided) => ({
      ...provided,
      width: "100%",
    }),
    control: (provided, state) => ({
      ...provided,
      minHeight: "58px",
      borderColor: errors.categoryId
        ? "#dc3545"
        : state.isFocused
        ? "#86b7fe"
        : "#dee2e6",
      borderRadius: "0.275rem",
      backgroundColor: "transparent",
      borderWidth: "0.1px",
      boxShadow: state.isFocused
        ? "0 0 0 0.25rem rgba(13, 110, 253, 0.25)"
        : "none",
      "&:hover": {
        borderColor: errors.categoryId
          ? "#dc3545"
          : state.isFocused
          ? "#86b7fe"
          : "#dee2e6",
      },
      paddingTop: "18px", // Make space for the floating label
    }),
    valueContainer: (provided) => ({
      ...provided,
      padding: "0 12px",
      paddingTop: "4px", // Adjust for floating label
    }),
    input: (provided) => ({
      ...provided,
      margin: "0",
      padding: "0",
      color: "#212529",
    }),
    placeholder: (provided) => ({
      ...provided,
      color: "#6c757d",
      marginTop: "-4px", // Adjust position
    }),
    singleValue: (provided) => ({
      ...provided,
      color: "#212529",
      marginTop: "-4px", // Adjust position
    }),
    indicatorSeparator: () => ({
      display: "none",
    }),
    dropdownIndicator: (provided) => ({
      ...provided,
      padding: "0px 14px",
      color: "#6c757d",
      "&:hover": {
        color: "#212529",
      },
    }),
    clearIndicator: (provided) => ({
      ...provided,
      padding: "0px 14px",
      color: "#6c757d",
      "&:hover": {
        color: "#212529",
      },
    }),
    menu: (provided) => ({
      ...provided,
      zIndex: 9999,
      marginTop: "2px",
    }),
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isSelected
        ? "#0d6efd"
        : state.isFocused
        ? "#e7f1ff"
        : "white",
      color: state.isSelected ? "white" : "#212529",
      paddingLeft: `${state.data.level * 20}px`,
      "&:active": {
        backgroundColor: state.isSelected ? "#0d6efd" : "#e7f1ff",
      },
    }),
  };

  // console.log("errors", errors);

  const flattenOptions = (nodes, level = 1) => {
    return nodes.reduce((acc, node) => {
      const label = `${" ".repeat(level - 1)} ${node.name}`;
      acc.push({ value: node.id, label, parent: node.parent, level });

      if (node.children && node.children.length > 0) {
        acc.push(...flattenOptions(node.children, level + 1));
      }

      return acc;
    }, []);
  };

  const createHierarchy = (data) => {
    const lookup = {};
    const result = [];

    data?.forEach((item) => {
      lookup[item.id] = { ...item, children: [] };
    });

    data?.forEach((item) => {
      const { id, parent } = item;
      if (parent === 0) {
        result.push(lookup[id]);
      } else if (lookup[parent]) {
        lookup[parent].children.push(lookup[id]);
      }
    });

    return result;
  };

  useEffect(() => {
    register("categoryId", { required: "Category is required" });
  }, [register]);

  useEffect(() => {
    if (state) {
      setTags(state.productTags || []);
      if (state.categoryId) {
        setValue("categoryId", state.categoryId);
      }
    }
  }, [state, setValue]);

  // Prepare category options
  const hierarchicalData = categoriesData
    ? createHierarchy(categoriesData?.data?.data)
    : [];
  const flatOptions = flattenOptions(hierarchicalData);

  // Set initial selected category
  useEffect(() => {
    if (flatOptions.length > 0 && state?.categoryId && !selectedCategory) {
      const foundOption = flatOptions.find(
        (option) => option.value == state.categoryId,
      );
      if (foundOption) {
        setSelectedCategory(foundOption);
      }
    }
  }, [flatOptions, state?.categoryId, selectedCategory]);

  const handleCategoryChange = (selectedOption) => {
    setSelectedCategory(selectedOption);
    setValue("categoryId", selectedOption ? selectedOption.value : "");
    if (selectedOption) {
      clearErrors("categoryId");
    } else {
      setValue("categoryId", "");
    }
  };

  const handleCategoryFocus = () => {
    clearErrors("categoryId");
  };

  useEffect(() => {
    setValue("tags", tags);
  }, [tags, setValue]);

  const handleKeyPress = (event) => {
    if (event.key === "Enter" && inputValue) {
      event.preventDefault();
      if (!tags.includes(inputValue.trim())) {
        setTags([...tags, inputValue.trim()]);
      }
      setInputValue("");
    }
  };

  const handleRemoveTag = (tagToRemove) => {
    setTags(tags.filter((tag) => tag !== tagToRemove));
  };

  const handleTagsFocus = () => {
    setIsTagsFocused(true);
  };

  const handleTagsBlur = () => {
    setIsTagsFocused(false);
  };

  const handleTagsClick = () => {
    if (tagsInputRef.current) {
      tagsInputRef.current.focus();
    }
  };

  const pricingAndStockWatch = watch("pricingAndStocks");
  const permutationFound = pricingAndStockWatch?.length > 0;

  const handleApplySalePriceToAll = () => {
    const salePrice = watch("salesPrice");
    const categoryId = watch("categoryId");

    if (salePrice && categoryId && permutationFound) {
      Swal.fire({
        title: "Are you sure?",
        text: "You want apply to all",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#3085d6",
        cancelButtonColor: "#d33",
        confirmButtonText: "Yes",
      }).then((result) => {
        if (result.isConfirmed) {
          const updatedInputs = pricingAndStockWatch.map((row) => ({
            ...row,
            originalPrice: salePrice,
          }));
          setValue("pricingAndStocks", updatedInputs);
        }
      });
    } else if (!salePrice) {
      toast.error("Sale Price is missing.", {
        position: customSettings.toastPosition,
        autoClose: customSettings.toastClose,
      });
    } else if (!categoryId) {
      toast.error("Category is missing.", {
        position: customSettings.toastPosition,
        autoClose: customSettings.toastClose,
      });
    } else {
      toast.error("Permutation is missing.", {
        position: customSettings.toastPosition,
        autoClose: customSettings.toastClose,
      });
    }
  };

  // Determine if tags field should show as "floating"
  const shouldFloatTags = isTagsFocused || tags.length > 0 || inputValue;

  return (
    <>
      {categoriesLoading || fieldSetLoading ? (
        <div className="d-flex py-3 justify-content-center align-items-center">
          <Button variant="falcon-default" size="sm" className="fs-10">
            Loading...
          </Button>
        </div>
      ) : (
        <Row className="gx-1 gy-1">
          <Col md="6">
            <Form.Group>
              <FloatingLabel controlId="floatingInput" label="Name">
                <Form.Control
                  type="text"
                  placeholder="Name"
                  title="Name"
                  maxLength={150}
                  isInvalid={!!errors?.name}
                  {...register("name")}
                />
                <Form.Control.Feedback type="invalid">
                  {errors?.name?.message}
                </Form.Control.Feedback>
              </FloatingLabel>
            </Form.Group>
          </Col>

          <Col md="6">
            {/* Custom Floating Label for react-select */}
            <Form.Group className="position-relative">
              <div
                className={`form-floating ${
                  errors.categoryId ? "is-invalid" : ""
                }`}
              >
                <div
                  className={`form-control p-0 ${
                    errors.categoryId ? "border-danger" : ""
                  }`}
                  style={{
                    minHeight: "58px",
                    border: errors.categoryId
                      ? "0px solid #dc3545"
                      : "0.1px solid #ffffff",
                    borderRadius: "0.375rem",
                    backgroundColor: "transparent",
                    cursor: "text",
                  }}
                >
                  <Select
                    options={flatOptions}
                    components={{ Option: CustomOption }}
                    isClearable
                    placeholder=""
                    styles={customStyles}
                    onChange={handleCategoryChange}
                    onFocus={handleCategoryFocus}
                    value={selectedCategory}
                    className="category-select"
                    classNamePrefix="select"
                    instanceId="category-select"
                  />
                </div>
                <label
                  className="form-label"
                  style={{
                    position: "absolute",
                    top: "0",
                    left: "0",
                    height: "100%",
                    padding: "1rem",
                    pointerEvents: "none",
                    border: "1px solid transparent",
                    transformOrigin: "0 0",
                    transition:
                      "opacity .1s ease-in-out, transform .1s ease-in-out",
                    color: "#6c757d",
                    zIndex: 2,
                  }}
                >
                  Category{" "}
                  {errors.categoryId && <span className="text-danger">*</span>}
                </label>
              </div>
              {errors.categoryId && (
                <div className="invalid-feedback d-block">
                  {errors.categoryId.message}
                </div>
              )}
            </Form.Group>
          </Col>

          <Col md="6">
            <Form.Group>
              <FloatingLabel
                label={
                  <>
                    <span>Store</span>
                    <span style={{ color: "red" }}> *</span>
                  </>
                }
              >
                <Form.Select
                  placeholder=" "
                  isInvalid={!!errors?.storeId}
                  {...register("storeId")}
                >
                  <option value="">Select Store</option>
                  {storesData?.data.data?.map((st) => (
                    <option key={st.id} value={st.id}>
                      {st.businessName}
                    </option>
                  ))}
                </Form.Select>
                <Form.Control.Feedback type="invalid">
                  {errors?.storeId?.message}
                </Form.Control.Feedback>
              </FloatingLabel>
            </Form.Group>
          </Col>

          <Col md="6">
            <Form.Group>
              <FloatingLabel label="Type">
                <Form.Select
                  placeholder=" "
                  isInvalid={!!errors?.type}
                  {...register("type")}
                >
                  <option value="">Select Type</option>
                  <option value="Storable">Storable</option>
                  <option value="Service">Service</option>
                  <option value="Consumable">Consumable</option>
                </Form.Select>
                <Form.Control.Feedback type="invalid">
                  {errors?.type?.message}
                </Form.Control.Feedback>
              </FloatingLabel>
            </Form.Group>
          </Col>

          <Col md="6">
            {/* Tags with Floating Label */}
            <Form.Group>
              <div
                className={`form-floating ${errors.tags ? "is-invalid" : ""}`}
              >
                <div
                  className={`form-control d-flex flex-wrap align-items-center ${
                    shouldFloatTags ? "tags-floating-active" : ""
                  }`}
                  style={{
                    minHeight: "58px",
                    paddingTop: shouldFloatTags ? "22px" : "0",
                    paddingLeft: "0.75rem",
                    paddingRight: "0.75rem",
                    paddingBottom: "0.375rem",
                    cursor: "text",
                    borderColor: errors.tags
                      ? "#dc3545"
                      : isTagsFocused
                      ? "#86b7fe"
                      : "#dee2e6",
                    backgroundColor: "transparent",
                    boxShadow: isTagsFocused
                      ? "0 0 0 0.25rem rgba(13, 110, 253, 0.25)"
                      : "none",
                  }}
                  onClick={handleTagsClick}
                  onFocus={handleTagsFocus}
                  onBlur={handleTagsBlur}
                  tabIndex={-1}
                >
                  {tags?.map((tag, index) => (
                    <SubtleBadge key={index} className="mr-2 mb-1 mx-1 mt-1">
                      {tag}
                      <span
                        className="ml-2"
                        style={{ cursor: "pointer" }}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleRemoveTag(tag);
                        }}
                      >
                        &times;
                      </span>
                    </SubtleBadge>
                  ))}
                  <input
                    ref={tagsInputRef}
                    type="text"
                    title="Tags"
                    className="border-0 flex-grow-1"
                    style={{
                      outline: "none",
                      color: "black",
                      minWidth: "120px",
                      backgroundColor: "transparent",
                      padding: "0",
                      margin: "0",
                      height: "auto",
                    }}
                    value={inputValue}
                    onChange={(e) => setInputValue(e.target.value)}
                    onKeyPress={handleKeyPress}
                    onFocus={handleTagsFocus}
                    onBlur={handleTagsBlur}
                    placeholder={shouldFloatTags ? "" : ""}
                  />
                </div>
                <label
                  className="form-label"
                  style={{
                    position: "absolute",
                    top: "0",
                    left: "0",
                    height: "100%",
                    padding: "1rem 1rem",
                    pointerEvents: "none",
                    border: "1px solid transparent",
                    transformOrigin: "0 0",
                    transition:
                      "opacity .1s ease-in-out, transform .1s ease-in-out",
                    color: "#6c757d",
                    zIndex: 2,
                  }}
                >
                  Tags
                </label>
              </div>
              {errors.tags && (
                <div className="invalid-feedback d-block">
                  {errors.tags.message}
                </div>
              )}
            </Form.Group>
          </Col>

          <Col md="6">
            <InputGroup>
              <FloatingLabel label="Sale Price" className="flex-grow-1">
                <Form.Control
                  type="number"
                  placeholder=" "
                  isInvalid={!!errors?.salesPrice}
                  {...register("salesPrice")}
                />
                <Form.Control.Feedback type="invalid">
                  {errors?.salesPrice?.message}
                </Form.Control.Feedback>
              </FloatingLabel>

              {/* <OverlayTrigger
                overlay={
                  <Tooltip
                    style={{ position: "fixed" }}
                    id="overlay-trigger-example"
                  >
                    Apply to All
                  </Tooltip>
                }
              >
                <Button
                  onClick={handleApplySalePriceToAll}
                  variant="secondary"
                  style={{ maxHeight: "58px", width: "54px" }}
                >
                  <MdOutlineLibraryAddCheck />
                </Button>
              </OverlayTrigger> */}
            </InputGroup>
          </Col>

          <Col md="6">
            <Form.Group>
              <FloatingLabel label="Purchase Price [Cost]">
                <Form.Control
                  type="number"
                  placeholder=" "
                  isInvalid={!!errors?.cost}
                  {...register("cost")}
                />
                <Form.Control.Feedback type="invalid">
                  {errors?.cost?.message}
                </Form.Control.Feedback>
              </FloatingLabel>
            </Form.Group>
          </Col>
          <Col md="6">
            <InputGroup>
              <FloatingLabel label="Custom Option">
                <Form.Select
                  placeholder=" "
                  isInvalid={!!errors?.customizationId}
                  {...register("customizationId")}
                  disabled={!customizable}
                >
                  <option value={"" || 0}>Select Custom Option</option>
                  {fieldSetData?.data?.data?.map((fs) => (
                    <option key={fs.id} value={fs.id}>
                      {fs.name}
                    </option>
                  ))}
                </Form.Select>

                <Form.Control.Feedback type="invalid">
                  {errors?.customizationId?.message}
                </Form.Control.Feedback>
              </FloatingLabel>

              {/* {customizable && customizationId && ( */}
              <Button
                variant="secondary"
                onClick={() => setFieldsModal(true)}
                title="View"
                style={{
                  display: customizable && customizationId ? "block" : "none",
                }}
              >
                <HiOutlineViewfinderCircle size={20} />
              </Button>
              {/* )} */}
            </InputGroup>
          </Col>

          <Col md="6">
            <Form.Check
              type="checkbox"
              id="defaultSwitch"
              label="Active"
              // className={!customizable && "mt-3"}
              defaultChecked={!state}
              isInvalid={!!errors.status}
              {...register("status")}
            />
          </Col>
          <Col
            md="6"
            // className={"d-flex justify-content-end align-items-center"}
          >
            <Form.Check
              type="checkbox"
              id="defaultSwitch2"
              label="Customizable"
              // className="mt-3"
              // defaultChecked={!state}
              isInvalid={!!errors.isCustomizable}
              {...register("isCustomizable")}
            />
          </Col>
        </Row>
      )}

      {/* {customizationId && (
        <> */}
      <FieldsModal
        show={fieldsModal}
        handleClose={() => setFieldsModal(false)}
        customizationId={customizationId}
      />
      {/* </>
      )} */}
    </>
  );
};

export default ProductGeneralInfo;

// <Col md="5" className="gx-0">
//   <Form.Group>
//     <FloatingLabel label="Custom Option">
//       <Form.Select
//         placeholder=" "
//         isInvalid={!!errors?.customizationId}
//         {...register("customizationId")}
//         // style={{ width: "775px" }}
//         disabled={!customizable}
//       >
//         <option value="">Select Custom Option</option>
//         {fieldSetData?.data?.data?.map((fs) => (
//           <option value={fs.id}>{fs.name}</option>
//         ))}
//       </Form.Select>
//       <Form.Control.Feedback type="invalid">
//         {errors?.customizationId?.message}
//       </Form.Control.Feedback>
//     </FloatingLabel>
//   </Form.Group>

// </Col>

// {customizationId && <>

//   <Col md={1} className="d-flex justify-content-end">
//     <OverlayTrigger
//       overlay={
//         <Tooltip
//           style={{ position: "fixed" }}
//           id="overlay-trigger-example"
//         >
//           View Fields of Custom Option
//         </Tooltip>
//       }
//     >
//       <Button
//         onClick={() => setFieldsModal(true)}
//         variant="secondary"
//         style={{ height: "100%", fontSize: "14px" }}
//       >
//         <HiOutlineViewfinderCircle size={20} />
//         {/* <MdOutlineLibraryAddCheck /> */}
//       </Button>
//     </OverlayTrigger>
//   </Col>
// </>}
